import React, { useState } from 'react'
import { Box } from '@chakra-ui/react'

import { OnSelectArgs } from './types'
import EmptyView from '~components/shared/empty-view'
import ClientSearchResult from './client-search-result'
import GradientDivider from '~components/shared/gradient-divider'
import { useSearchClients } from '~utils/hooks/use-search-clients'
import SharedStack from '~components/shared/wrappers/shared-stack'
import AsyncWrapper from '~components/shared/wrappers/async-wrapper'
import { scrollbarCSS } from '~components/dashboard/action-item-list'
import SharedSearchInput from '~components/shared/shared-search-input'
import {
    ClientPreviewFieldsFragment,
    ClinicFieldsFragment,
} from '~graphql/generated/graphql'
import Pagination from '~components/shared/pagination'
import {
    ShowInactiveSwitch,
    ShowNonSystemSwitch,
} from '~components/booking/clinicians/actions'
import { useStateWithDebounce } from '~utils/hooks/use-state-with-debounce'
import { useLocalStorage } from '~utils/hooks/use-local-storage'
import { LocalStorageKeys } from '~config/constants'

interface ClientSearchContentProps {
    clinic: ClinicFieldsFragment
    onSelect: (args: OnSelectArgs) => void
}

export default function ClientSearchContent({
    clinic,
    onSelect,
}: ClientSearchContentProps) {
    const [value, setValue] = useState<string>('')
    const [showNonSystem, setShowNonSystem] = useLocalStorage<boolean>(
        LocalStorageKeys.showNonSystemClients,
        false,
    )
    const [showInactive, setShowInactive, debouncedShowInactive] =
        useStateWithDebounce<boolean>(false, 500)

    const {
        clientResults,
        loading,
        count,
        page,
        setPage,
        pageSize,
        setPageSize,
    } = useSearchClients({
        clinic,
        query: value,
        showInactive: debouncedShowInactive,
        showNonSystem,
    })

    function handleClientSelect(client: ClientPreviewFieldsFragment) {
        onSelect({ client, clinic })
        setValue('')
        setShowInactive(false)
    }

    return (
        <>
            <SharedStack>
                <Box w="full">
                    <SharedSearchInput
                        value={value}
                        onChange={e => setValue(e.target.value)}
                        autoFocus
                        w="100%"
                        size="sm"
                        bg="gray.100"
                        color="gray.800"
                        borderRadius="full"
                        _placeholder={{ color: 'gray.400' }}
                        placeholder="Search clients and patients..."
                    />
                </Box>
                <SharedStack
                    row
                    justifyContent={{ base: 'center', md: 'flex-end' }}
                >
                    <ShowNonSystemSwitch
                        showNonSystem={showNonSystem}
                        setShowNonSystem={setShowNonSystem}
                    />
                    <ShowInactiveSwitch
                        showInactive={showInactive}
                        setShowInactive={setShowInactive}
                    />
                </SharedStack>
            </SharedStack>
            <AsyncWrapper
                loading={loading}
                empty={clientResults.length === 0}
                emptyComponent={<EmptyClientsComponent value={value} />}
            >
                <SharedStack
                    my={4}
                    spacing={2}
                    css={scrollbarCSS}
                    divider={<GradientDivider />}
                >
                    {clientResults.map((res, idx) => (
                        <ClientSearchResult
                            key={res.client?.id || idx}
                            res={res}
                            value={value}
                            onClick={handleClientSelect}
                            clinic={clinic}
                        />
                    ))}
                </SharedStack>
                <Pagination
                    page={page}
                    setPage={setPage}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    count={count}
                    layoutOverrides={{
                        w: 'full',
                        justifyContent: 'space-between',
                        pb: 2,
                    }}
                />
            </AsyncWrapper>
        </>
    )
}

function EmptyClientsComponent({ value }: { value: string }) {
    return (
        <EmptyView
            px={6}
            header={value ? undefined : 'Start typing to search clients...'}
            infoText={
                value
                    ? 'No results returned. Ensure your filters are properly set and try again.'
                    : 'You can search by client name or patient name.'
            }
        />
    )
}
